// Under-13 COPPA-Compliant Consent Form
// Single source of truth for labels and defaults
// Based on COPPA (Children's Online Privacy Protection Act) requirements

export const UNDER_13_CONSENT_QUESTIONS = [
  {
    id: 'account_info_collection',
    defaultValue: true,
    required: true, // Cannot be disabled - required for account functionality
    label: "Collection of Account Information",
    note: 'We collect your child\'s name, email address, date of birth, and age to create and manage their account. This information is required for account functionality and cannot be disabled.',
    category: 'collection',
    coppaSection: 'What We Collect'
  },
  {
    id: 'profile_info_collection',
    defaultValue: true,
    required: false,
    label: "Collection of Profile Information",
    note: 'We collect profile picture and display preferences. You can choose whether this information is displayed publicly or kept private.',
    category: 'collection',
    coppaSection: 'What We Collect'
  },
  {
    id: 'content_collection',
    defaultValue: true,
    required: true, // Cannot be disabled - core functionality
    label: "Collection of Creative Content",
    note: 'We collect your child\'s artwork, stories, and activity submissions to provide the core learning and creative features. This is required for the platform to function.',
    category: 'collection',
    coppaSection: 'What We Collect'
  },
  {
    id: 'usage_data_collection',
    defaultValue: true,
    required: false,
    label: "Collection of Usage Data",
    note: 'We collect information about how your child uses the platform (features accessed, time spent, preferences) to improve our services and personalize their experience. This can be disabled.',
    category: 'collection',
    coppaSection: 'What We Collect'
  },
  {
    id: 'pii_public_allowed',
    defaultValue: false, // Changed default to false for safety
    required: false,
    label: "Allow my child's real name to be displayed publicly",
    note: 'If OFF → we will use a nickname or username only. This protects your child\'s privacy when their work is shared.',
    category: 'disclosure',
    coppaSection: 'Public Disclosure'
  },
  {
    id: 'profile_pic_allowed',
    defaultValue: false, // Changed default to false for safety
    required: false,
    label: "Allow my child's profile picture to be displayed publicly",
    note: 'If OFF → we will use a predefined avatar. This protects your child\'s privacy.',
    category: 'disclosure',
    coppaSection: 'Public Disclosure'
  },
  {
    id: 'artwork_public_allowed',
    defaultValue: false, // Changed default to false for safety
    required: false,
    label: "Allow my child's artwork to be shared publicly",
    note: 'If OFF → artwork will only be visible to your child and approved mentors/teachers. Art metadata will not contain any personally identifiable information (PII).',
    category: 'disclosure',
    coppaSection: 'Public Disclosure'
  },
  {
    id: 'third_party_sharing',
    defaultValue: true,
    required: false,
    label: "Sharing with Service Providers",
    note: 'We share information with trusted service providers (Firebase for data storage, payment processors for subscriptions, email services for communications) who help us operate the platform. These providers are contractually required to protect your child\'s information.',
    category: 'sharing',
    coppaSection: 'Third-Party Sharing'
  },
  {
    id: 'marketing_communications',
    defaultValue: false, // Default to false - opt-in only
    required: false,
    label: "Marketing Communications",
    note: 'Allow Artmosphere to send promotional emails about new features, educational content, and special offers. You can opt out at any time.',
    category: 'communications',
    coppaSection: 'Communications'
  }
];

// COPPA Required Disclosures
export const COPPA_DISCLOSURES = {
  whatWeCollect: {
    title: "What Personal Information We Collect",
    items: [
      "Account Information: Name, email address, date of birth, age",
      "Profile Information: Profile picture, display name, preferences",
      "Creative Content: Artwork, stories, activity submissions, comments",
      "Usage Data: Features accessed, time spent, learning progress, preferences",
      "Technical Data: Device information, IP address (for security purposes only)"
    ]
  },
  howWeUse: {
    title: "How We Use Your Child's Information",
    items: [
      "To create and manage your child's account",
      "To provide educational and creative features (art creation, storyboarding, art history learning)",
      "To personalize the learning experience",
      "To communicate about account activity and important updates",
      "To ensure platform security and prevent fraud",
      "To comply with legal obligations"
    ]
  },
  thirdPartySharing: {
    title: "Third-Party Services We Use",
    items: [
      "Firebase (Google): Data storage and authentication - subject to Google's privacy policy",
      "Payment Processors: For subscription management - payment information is not stored by Artmosphere",
      "Email Services: For sending account-related and educational communications",
      "Analytics Services: To understand how the platform is used and improve our services (no personal information shared)"
    ],
    note: "All third-party services are contractually required to protect your child's information and comply with COPPA."
  },
  parentalRights: {
    title: "Your Parental Rights Under COPPA",
    items: [
      "Right to Review: You can review all personal information we have collected about your child",
      "Right to Delete: You can request deletion of your child's personal information at any time",
      "Right to Refuse: You can refuse further collection or use of your child's information",
      "Right to Revoke: You can revoke this consent at any time by contacting us",
      "Right to Access: You can access and modify your child's account settings and privacy preferences"
    ],
    contact: "To exercise these rights, contact us at: support@artmosphere.com"
  },
  dataRetention: {
    title: "Data Retention and Deletion",
    description: "We retain your child's information only as long as necessary to provide our services. When you request deletion, we will delete all personal information within 30 days, except where we are required to retain it for legal or security purposes."
  },
  security: {
    title: "How We Protect Your Child's Information",
    description: "We use industry-standard security measures including encryption, secure servers, and access controls to protect your child's information. However, no method of transmission over the internet is 100% secure."
  }
};

// Consent categories for organization
export const CONSENT_CATEGORIES = {
  collection: 'Data Collection',
  disclosure: 'Public Disclosure',
  sharing: 'Third-Party Sharing',
  communications: 'Communications'
};

export const ALL_TOGGLES_KEYS = UNDER_13_CONSENT_QUESTIONS.map(q => q.id);

// Required consents that cannot be disabled
export const REQUIRED_CONSENTS = UNDER_13_CONSENT_QUESTIONS
  .filter(q => q.required === true)
  .map(q => q.id);

// Optional consents that can be toggled
export const OPTIONAL_CONSENTS = UNDER_13_CONSENT_QUESTIONS
  .filter(q => q.required === false)
  .map(q => q.id);
